import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Coins, Plus, RefreshCw, Save, Settings2 } from "lucide-react";

import api from "../api";
import { useMasterData } from "../../utils/useMasterData";

const BALANCE_TYPES = ["DR", "CR"];

const EMPTY_FORM = {
  account_code: "",
  account_name: "",
  account_type: "ASSET",
  account_subtype: "",
  parent_id: "",
  opening_balance: 0,
  balance_type: "DR",
};

function buildTree(accounts, parentId = null, level = 0) {
  return accounts
    .filter((account) => (account.parent_id || null) === parentId)
    .sort((a, b) => String(a.account_code).localeCompare(String(b.account_code)))
    .flatMap((account) => [
      { ...account, level },
      ...buildTree(accounts, account.id, level + 1),
    ]);
}

function ChartOfAccounts() {
  const { getValues } = useMasterData("ACCOUNT_TYPE");
  const accountTypes = getValues("ACCOUNT_TYPE");
  const [accounts, setAccounts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showOpeningModal, setShowOpeningModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [openingForm, setOpeningForm] = useState({ id: null, account_name: "", opening_balance: 0 });
  const [filterType, setFilterType] = useState("");

  const load = useCallback(async (type = filterType) => {
    setLoading(true);
    try {
      const query = type ? `?account_type=${type}` : "";
      const res = await api.get(`/accounts/ledger/coa${query}`);
      const rows = res.data.data || [];
      setAccounts(rows);
      setExpanded((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if ((row.children_count || 0) > 0 && next[row.id] === undefined) {
            next[row.id] = true;
          }
        });
        return next;
      });
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load chart of accounts.");
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    load();
  }, [load]);

  const visibleRows = useMemo(() => {
    const treeRows = buildTree(accounts);
    const accountById = new Map(accounts.map((account) => [account.id, account]));
    return treeRows.filter((row) => {
      let currentParentId = row.parent_id;
      while (currentParentId) {
        if (expanded[currentParentId] === false) {
          return false;
        }
        const parent = accountById.get(currentParentId);
        currentParentId = parent?.parent_id || null;
      }
      return true;
    });
  }, [accounts, expanded]);

  const parentOptions = useMemo(
    () => accounts.filter((account) => !form.account_type || account.account_type === form.account_type),
    [accounts, form.account_type]
  );

  const seedSystemAccounts = async () => {
    try {
      const res = await api.post("/accounts/ledger/coa/seed-system");
      setMessage(res.data.message || "System accounts seeded.");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to seed system accounts.");
    }
  };

  const saveAccount = async () => {
    setSaving(true);
    try {
      await api.post("/accounts/ledger/coa", {
        ...form,
        parent_id: form.parent_id ? Number(form.parent_id) : null,
        opening_balance: Number(form.opening_balance || 0),
      });
      setShowAddModal(false);
      setForm(EMPTY_FORM);
      setMessage("Account created.");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create account.");
    } finally {
      setSaving(false);
    }
  };

  const saveOpeningBalance = async () => {
    setSaving(true);
    try {
      await api.put(`/accounts/ledger/coa/${openingForm.id}`, {
        opening_balance: Number(openingForm.opening_balance || 0),
      });
      setShowOpeningModal(false);
      setMessage("Opening balance updated.");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update opening balance.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totals = useMemo(() => {
    return accounts.reduce(
      (acc, account) => {
        acc.opening += Number(account.opening_balance || 0);
        acc.current += Number(account.current_balance || 0);
        return acc;
      },
      { opening: 0, current: 0 }
    );
  }, [accounts]);

  return (
    <div className="space-y-6">
      {(message || error) && (
        <div className={`rounded-2xl border px-4 py-3 text-sm ${error ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {error || message}
        </div>
      )}

      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard icon={Coins} label="Accounts" value={accounts.length} />
        <SummaryCard icon={Settings2} label="Opening Balance" value={`Rs. ${totals.opening.toFixed(2)}`} />
        <SummaryCard icon={RefreshCw} label="Live Balance" value={`Rs. ${totals.current.toFixed(2)}`} />
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-slate-900">Chart of Accounts</h3>
            <p className="text-sm text-slate-500">Collapsible account tree with live balances, system seeding, and opening balance control.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                load(e.target.value);
              }}
              className="input"
            >
              <option value="">All Types</option>
              {accountTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <button onClick={() => load()} className="secondary-btn"><RefreshCw size={15} /> Refresh</button>
            <button onClick={seedSystemAccounts} className="secondary-btn"><Settings2 size={15} /> Seed System Accounts</button>
            <button onClick={() => setShowAddModal(true)} className="primary-btn"><Plus size={15} /> Add Account</button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Balance</th>
                <th className="px-4 py-3">Dr/Cr</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {!loading && visibleRows.map((account) => {
                const hasChildren = accounts.some((item) => item.parent_id === account.id);
                return (
                  <tr key={account.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold text-indigo-700">{account.account_code}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2" style={{ paddingLeft: `${account.level * 20}px` }}>
                        {hasChildren ? (
                          <button onClick={() => toggle(account.id)} className="icon-btn">
                            {expanded[account.id] === false ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                          </button>
                        ) : (
                          <span className="inline-block w-4" />
                        )}
                        <span>{account.account_name}</span>
                        {account.is_system && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">System</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">{account.account_type}</td>
                    <td className="px-4 py-3 font-semibold">Rs. {Number(account.current_balance || 0).toFixed(2)}</td>
                    <td className="px-4 py-3">{account.balance_type}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setOpeningForm({
                              id: account.id,
                              account_name: `${account.account_code} - ${account.account_name}`,
                              opening_balance: account.opening_balance || 0,
                            });
                            setShowOpeningModal(true);
                          }}
                          className="table-btn"
                        >
                          Set Opening Balance
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !visibleRows.length && (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-slate-500">No accounts found.</td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan="6" className="px-4 py-10 text-center text-slate-500">Loading accounts...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {showAddModal && (
        <Modal
          title="Add Account"
          onClose={() => setShowAddModal(false)}
          footer={(
            <>
              <button onClick={() => setShowAddModal(false)} className="secondary-btn">Cancel</button>
              <button onClick={saveAccount} disabled={saving} className="primary-btn"><Save size={15} /> Save Account</button>
            </>
          )}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Account Code">
              <input value={form.account_code} onChange={(e) => setForm((prev) => ({ ...prev, account_code: e.target.value }))} className="input" />
            </Field>
            <Field label="Account Name">
              <input value={form.account_name} onChange={(e) => setForm((prev) => ({ ...prev, account_name: e.target.value }))} className="input" />
            </Field>
            <Field label="Type">
              <select value={form.account_type} onChange={(e) => setForm((prev) => ({ ...prev, account_type: e.target.value, parent_id: "" }))} className="input">
                {accountTypes.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </Field>
            <Field label="Subtype">
              <input value={form.account_subtype} onChange={(e) => setForm((prev) => ({ ...prev, account_subtype: e.target.value }))} className="input" />
            </Field>
            <Field label="Parent Account">
              <select value={form.parent_id} onChange={(e) => setForm((prev) => ({ ...prev, parent_id: e.target.value }))} className="input">
                <option value="">None</option>
                {parentOptions.map((account) => (
                  <option key={account.id} value={account.id}>{account.account_code} - {account.account_name}</option>
                ))}
              </select>
            </Field>
            <Field label="Balance Type">
              <select value={form.balance_type} onChange={(e) => setForm((prev) => ({ ...prev, balance_type: e.target.value }))} className="input">
                {BALANCE_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
              </select>
            </Field>
            <Field label="Opening Balance" className="md:col-span-2">
              <input type="number" value={form.opening_balance} onChange={(e) => setForm((prev) => ({ ...prev, opening_balance: e.target.value }))} className="input" />
            </Field>
          </div>
        </Modal>
      )}

      {showOpeningModal && (
        <Modal
          title="Set Opening Balance"
          onClose={() => setShowOpeningModal(false)}
          footer={(
            <>
              <button onClick={() => setShowOpeningModal(false)} className="secondary-btn">Cancel</button>
              <button onClick={saveOpeningBalance} disabled={saving} className="primary-btn"><Save size={15} /> Save Balance</button>
            </>
          )}
        >
          <div className="grid gap-4">
            <Field label="Account">
              <input value={openingForm.account_name} disabled className="input bg-slate-50" />
            </Field>
            <Field label="Opening Balance">
              <input type="number" value={openingForm.opening_balance} onChange={(e) => setOpeningForm((prev) => ({ ...prev, opening_balance: e.target.value }))} className="input" />
            </Field>
          </div>
        </Modal>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .primary-btn, .secondary-btn, .table-btn, .icon-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }
        .primary-btn { background: rgb(79 70 229); color: white; }
        .secondary-btn, .table-btn { border: 1px solid rgb(203 213 225); background: white; color: rgb(51 65 85); }
        .icon-btn {
          padding: 0;
          border: none;
          background: transparent;
          color: rgb(71 85 105);
        }
      `}</style>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
        <Icon size={16} />
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Modal({ title, children, onClose, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-lg font-bold text-slate-900">{title}</h4>
            <button onClick={onClose} className="table-btn">Close</button>
          </div>
        </div>
        <div className="p-6">{children}</div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">{footer}</div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

export default ChartOfAccounts;
